import React from 'react';
import {
  Button,
  Form,
  Gutter,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Toggle,
} from '@axiom/ui';
import type {
  EnvoyCandidateSearchFilters,
  UserCandidateSearch,
  UserOpportunitySearch,
  UserSearch,
} from '@axiom/validation';
import {
  EnvoyCandidateSearchFiltersFormSchemaFields,
  EnvoyCandidateSearchFiltersSaveFormSchema,
} from '@axiom/validation';

import { UserAlertsApi } from '../../api/user-alerts-api';
import { UserSearchesApi } from '../../api/user-searches-api';

export const SaveFiltersModal = ({
  filters,
  onClose,
  savedSearch,
  type,
}: {
  filters?: UserCandidateSearch['search'] | UserOpportunitySearch['search'];
  onClose: () => void;
  savedSearch?: UserSearch;
  type: UserSearch['type'];
}) => {
  return (
    <Form
      initialValues={{
        createAlert: savedSearch?.alertActive ?? true,
        name: savedSearch?.name ?? '',
      }}
      name="SaveFiltersModal"
      schema={EnvoyCandidateSearchFiltersSaveFormSchema}
      onSubmit={async (_changedData, _actions, values) => {
        const { createAlert, name } = values;
        const alertActive = createAlert ?? false;

        if (savedSearch?.id) {
          await UserSearchesApi.updateUserSearch(savedSearch.id, {
            alertActive,
            id: savedSearch.id,
            name,
          });
        } else {
          const savedFilters = Object.fromEntries(
            Object.entries(filters ?? []).filter(
              ([_, v]) =>
                v !== null &&
                !(typeof v === 'object' && Object.keys(v).length === 0)
            )
          );

          Object.keys(EnvoyCandidateSearchFiltersFormSchemaFields)
            .filter(k => !Object.keys(savedFilters).includes(k))
            .forEach(key => {
              savedFilters[key] = null;
            });

          await UserSearchesApi.createUserSearch({
            alertActive,
            name,
            search: savedFilters as EnvoyCandidateSearchFilters,
            type,
          });
        }

        await UserAlertsApi.refreshUserAlerts();
        await UserSearchesApi.refreshUserSearches(type);

        onClose();
      }}
    >
      {({ fireSubmit }) => (
        <Modal>
          <ModalHeader
            name="SaveFiltersModalHeader"
            onClose={() => {
              onClose();
            }}
          >
            Saved Search
          </ModalHeader>
          <ModalSection>
            <Input name="name" label="Search Name" />
            <Gutter vertical="8px" />
            <Toggle
              description={
                type === 'candidate'
                  ? 'Get weekly emails when new talent match this search.'
                  : 'Get weekly emails when new opportunities match this search.'
              }
              name="createAlert"
              label="Create an alert for this search?"
            />
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_BUTTON"
              onClick={() => {
                onClose();
              }}
              variation="outline"
            >
              Cancel
            </Button>
            <Button name="SAVE_BUTTON" onClick={fireSubmit}>
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
